import React, { useCallback, useEffect } from "react";
import StatusTable from "../components/StatusTable/StatusTable";
import Pagination from "../components/Pagination/Pagination";
import { AssetsResponse } from "../types/statusTypes";
import { fetchAndMapAssetsAndStatuses } from "../helpers/apiHelper";
import ErrorView from "./ErrorView";
import Loading from "../components/Loading";
import { useApiErrorHandler } from "../hooks/useApiErrorHandler";
import SearchForm from "../components/SearchForm";
import { useSearchParams } from "react-router-dom";

const PAGE_SIZE = 15;

export default function ListView() {
  const { loading, error, response, fetchData } = useApiErrorHandler<AssetsResponse>();
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") ?? "";
  const currentPage = Number(searchParams.get("page") ?? 1);
  const totalPages = Math.ceil(Math.max(response?.contentStatuses.length ?? 1, 1) / PAGE_SIZE);

  const setPage = useCallback((page: number) => {
    searchParams.set("page", page.toString());
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchData(fetchAndMapAssetsAndStatuses({ search }));
  }, [fetchData, search]);

  useEffect(() => {
    if (response) {
      if (Number.isNaN(currentPage) || currentPage < 1) {
        setPage(1);
      }
      else if (currentPage > totalPages) {
        setPage(totalPages);
      }
    }
  }, [response, currentPage, setPage, totalPages]);

  function getPage(page: number) {
    const start = (page - 1) * PAGE_SIZE;
    return response?.contentStatuses.slice(start, start + PAGE_SIZE) ?? [];
  }

  const hasResponseAndStatuses = response && response.contentStatuses.length > 0;
  const errorMessage = search ? `No results found for "${search}"` : "No processes found";

  return (
    <div className="p-5">
      <SearchForm fetchData={fetchData} />
      {loading && <Loading />}
      {!loading && error && (
        <ErrorView hasHomeButton={false} message={`No processes found: ${error}`} />)}
      {!loading && !error && !hasResponseAndStatuses && (
        <ErrorView hasHomeButton={false} message={errorMessage} />)}
      {!loading && !error && hasResponseAndStatuses && (
        <>
          <StatusTable contentStatuses={getPage(currentPage)} />
          <div className="d-flex justify-content-end pe-5">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
}
